import { parseCivilDate } from '../date';

const CIVIL_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function deserializeBooleans<T extends Record<string, any>>(
  data: T,
  booleanFields: (keyof T)[]
): T {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const result = { ...data } as any;

  booleanFields.forEach((field) => {
    const key = field as string;
    if (key in result && typeof result[key] === 'string') {
      if (result[key] === 'true') {
        result[key] = true;
      } else if (result[key] === 'false') {
        result[key] = false;
      }
    }
  });

  return result as T;
}

export type QueryPrimitive = string | number | boolean | Date | null | undefined;
export type QueryValue = QueryPrimitive | QueryPrimitive[];

export interface QueryBuildOptions {
  skipNull?: boolean;
  skipUndefined?: boolean;
  skipEmptyString?: boolean;
  arrayFormat?: 'repeat' | 'comma';
  dateSerializer?: (date: Date) => string;
}

function shouldSkipValue(value: QueryPrimitive, options: Required<QueryBuildOptions>): boolean {
  if (value === null) {
    return options.skipNull;
  }

  if (value === undefined) {
    return options.skipUndefined;
  }

  if (value === '') {
    return options.skipEmptyString;
  }

  return false;
}

function serializeQueryValue(
  value: Exclude<QueryPrimitive, null | undefined>,
  options: Required<QueryBuildOptions>
): string {
  if (value instanceof Date) {
    return options.dateSerializer(value);
  }

  return String(value);
}

export function buildSearchParams<T extends object>(
  query?: T,
  options?: QueryBuildOptions
): URLSearchParams {
  const config: Required<QueryBuildOptions> = {
    skipNull: options?.skipNull ?? true,
    skipUndefined: options?.skipUndefined ?? true,
    skipEmptyString: options?.skipEmptyString ?? true,
    arrayFormat: options?.arrayFormat ?? 'repeat',
    dateSerializer: options?.dateSerializer ?? ((date) => date.toISOString()),
  };

  const params = new URLSearchParams();

  if (!query) {
    return params;
  }

  Object.entries(query).forEach(([key, rawValue]) => {
    const value = rawValue as QueryValue;

    if (Array.isArray(value)) {
      const validValues = value.filter((value) => !shouldSkipValue(value, config)) as Array<
        Exclude<QueryPrimitive, null | undefined>
      >;

      if (validValues.length === 0) {
        return;
      }

      if (config.arrayFormat === 'comma') {
        params.append(
          key,
          validValues.map((value) => serializeQueryValue(value, config)).join(',')
        );
        return;
      }

      validValues.forEach((value) => {
        params.append(key, serializeQueryValue(value, config));
      });
      return;
    }

    if (shouldSkipValue(value, config)) {
      return;
    }

    if (value === null || value === undefined) {
      return;
    }

    params.append(key, serializeQueryValue(value, config));
  });

  return params;
}

export function withQuery<T extends object>(
  path: string,
  query?: T,
  options?: QueryBuildOptions
): string {
  const params = buildSearchParams(query, options);
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
}

export function buildTruthySearchParams<T extends object>(
  query: T,
  keys: readonly (keyof T)[]
): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of keys) {
    const value = query[key];

    if (value === undefined || value === null || (typeof value === 'string' && value === '')) {
      continue;
    }

    params.append(String(key), String(value));
  }

  return params;
}

export function deserializeDates<T extends Record<string, any>>(
  data: T,
  dateFields: (keyof T)[]
): T {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const result = { ...data } as any;

  dateFields.forEach((field) => {
    const key = field as string;
    if (key in result && result[key] && typeof result[key] === 'string') {
      const rawValue = result[key] as string;
      if (CIVIL_DATE_REGEX.test(rawValue)) {
        const civilDate = parseCivilDate(rawValue);
        if (civilDate) {
          result[key] = civilDate;
        }
        return;
      }
      const date = new Date(rawValue);
      if (!Number.isNaN(date.getTime())) {
        result[key] = date;
      }
    }
  });

  return result as T;
}
